import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { ApiService } from '../../../api/api.service';
import { agricolaGuroDesignConfig } from '../../../config/agricola-guro-design.config';
import { AgricolaFirebaseSyncService } from '../../../services/agricola-firebase-sync.service';

type PublicPaymentState =
  | 'loading'
  | 'pending'
  | 'signedWaiting'
  | 'emitted'
  | 'alreadyPurchased'
  | 'error';

// Customer-facing public signature page for the validate_signature_by_seller flow.
// The customer reviews read-only data, signs, and the signature is saved (status=2);
// the seller then validates and emits (Part A). This page waits on Firebase until the
// seller emits, then shows the purchase summary. All upstream work is proxied by our
// apiserver (which sets the Referer for S2's @private_link).
@Component({
  selector: 'app-public-payment-form',
  templateUrl: './public-payment-form.component.html',
  styleUrls: ['./public-payment-form.component.css'],
  standalone: false
})
export class PublicPaymentFormComponent implements OnInit, OnDestroy {
  state: PublicPaymentState = 'loading';
  errorMessage = '';

  readonly brandName = agricolaGuroDesignConfig.name;
  readonly logoMain = agricolaGuroDesignConfig.logos.main;
  readonly logoFooter = agricolaGuroDesignConfig.logos.footer;
  readonly poweredBy = agricolaGuroDesignConfig.logos.poweredBy;

  token = '';
  origin = 'externo';
  emissionLogId: number | null = null;

  customer: Record<string, string> = {};
  dependents: { name: string; identification: string; birthday: string }[] = [];
  planName = '';

  // Thank-you (emitted / already-purchased) data.
  purchaseDate = '';
  customerInfo: Record<string, string> = {};
  planSummary: { name: string; description: string; formattedPrice: string; effectiveDate: string } = {
    name: '', description: '', formattedPrice: '', effectiveDate: ''
  };
  showPrintS2 = false;
  isPrintingDocument = false;
  documentStatusMessage = '';
  documentStatusType: 'success' | 'error' = 'success';

  signatureImage: string | null = null;
  submitting = false;
  // Set when the seller rejects the signature and the customer is sent back to sign.
  rejectionReason = '';

  policyNumber = '';
  transactionNumber = '';

  private dataToSend: any = null;
  private planInfo: any = null;
  private completionSubscription: Subscription | null = null;

  constructor(
    private readonly api: ApiService,
    private readonly route: ActivatedRoute,
    private readonly firebaseSync: AgricolaFirebaseSyncService
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.token = params.get('id') || params.get('keyId') || '';
    this.origin = params.get('origin') || 'externo';

    if (!this.token) {
      this.fail('El enlace no contiene la información necesaria para continuar.');
      return;
    }

    void this.loadLink();
  }

  ngOnDestroy(): void {
    this.completionSubscription?.unsubscribe();
  }

  private async loadLink(): Promise<void> {
    try {
      const raw: any = await firstValueFrom(
        this.api.agricola.getPublicAssistanceLink(this.token).observable<any>()
      );
      const link = this.unwrapEnvelope(raw);

      if (!link || Number(link.code) !== 200) {
        this.fail('El enlace no existe o ha expirado.');
        return;
      }

      this.emissionLogId = Number(link.emission_log_id) || null;
      this.policyNumber = String(link.policy_number || '');
      // The document endpoint needs both segments; if no transaction came back,
      // fall back to the policy number (per the S2 thank-you resolution rule).
      this.transactionNumber = String(link.transaction_number || link.transactionId || '') || this.policyNumber;

      const status = Number(link.status);
      if (status === 1) {
        this.state = 'alreadyPurchased';
        this.parseSaleData(link);
        return;
      }

      this.parseSaleData(link);

      if (status === 2) {
        this.enterWaiting();
        return;
      }

      this.state = 'pending';
    } catch {
      this.fail('No se pudo conectar con el servidor. Intenta de nuevo.');
    }
  }

  private parseSaleData(link: any): void {
    try {
      const parsed = typeof link.data === 'string' ? JSON.parse(link.data) : link.data;
      this.dataToSend = parsed?.dataToSend || {};
      this.planInfo = parsed?.planInfo || {};
    } catch {
      this.dataToSend = {};
      this.planInfo = {};
    }

    const customer = (this.dataToSend?.customers && this.dataToSend.customers[0]) || {};
    this.customer = {
      // Document type comes from extraData (key document_type, e.g. "dui").
      identificationType: this.documentTypeLabel(this.extraDataValue('document_type')) || this.str(customer.identificationType),
      nationalId: this.str(customer.nationalId),
      birthDate: this.str(customer.birthDate),
      firstName: this.str(customer.firstName),
      lastName: this.str(customer.lastName),
      gender: this.genderLabel(customer.gender),
      phone: this.str(customer.phoneNumber1),
      email: this.str(customer.emailAddress),
      // Default residence to El Salvador (Agricola / BAES) when not provided.
      country: this.str(customer.country || customer.residenceCountry) || 'El Salvador'
    };

    this.customerInfo = {
      fullName: [customer.firstName, customer.middleName, customer.lastName, customer.secondLastName]
        .map((v: unknown) => this.str(v)).filter(Boolean).join(' '),
      nationalId: this.str(customer.nationalId),
      email: this.str(customer.emailAddress),
      phone: this.str(customer.phoneNumber1 || customer.phoneNumber2),
      gender: this.genderLabel(customer.gender),
      address: this.str(customer.address)
    };

    this.dependents = (this.dataToSend?.dependents || []).map((dep: any) => ({
      name: this.str(dep.name || `${dep.first_name || ''} ${dep.last_name || ''}`.trim()),
      identification: this.str(dep.identification_id || dep.nationalId),
      birthday: this.formatBirthday(dep.birthday || dep.birthDate || dep.birth_date)
    }));

    this.planName = this.str(this.planInfo?.plan_name || this.planInfo?.plan);

    const mergedConfig = {
      ...(this.planInfo?.config || {}),
      ...(this.planInfo?.product_config || {}),
      ...(this.dataToSend?.plan_config || {})
    };
    const price = Number(this.planInfo?.price ?? this.dataToSend?.premium ?? 0);
    const currency = this.str(this.dataToSend?.currency || this.planInfo?.currency || 'USD');
    this.planSummary = {
      name: this.str(
        this.dataToSend?.tranTypeDescription || this.planInfo?.plan_name || this.dataToSend?.product_code || 'Plan de asistencia'
      ),
      description: this.str(this.planInfo?.description || this.dataToSend?.tranTypeDescription),
      formattedPrice: this.formatPrice(price, currency),
      effectiveDate: this.str(this.dataToSend?.date_effective || this.dataToSend?.effective_date)
    };
    this.showPrintS2 = this.isEnabled(mergedConfig['print_sale_without_signature']);
    this.purchaseDate = new Date().toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  onSignatureSaved(image: string | null): void {
    this.signatureImage = image;
  }

  get canSubmit(): boolean {
    return !this.submitting && !!this.signatureImage;
  }

  async submitSignature(): Promise<void> {
    if (!this.canSubmit || !this.token) {
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    try {
      const uploadData = {
        ...this.dataToSend,
        plan_config: {
          ...(this.planInfo?.config || {}),
          ...(this.planInfo?.product_config || {})
        },
        currency: this.planInfo?.currency,
        digital_signature: true
      };

      const formData = new FormData();
      formData.append('uploadFile', this.dataURLtoBlob(this.signatureImage as string), 'firma-asistencia.png');
      formData.append('uploadData', JSON.stringify(uploadData));

      const rawResponse: any = await firstValueFrom(
        this.api.agricola.savePublicAssistanceSignature(this.token, formData).observable<any>()
      );
      const response = this.unwrapEnvelope(rawResponse);

      if (!response || Number(response.code) !== 200) {
        this.errorMessage = this.extractError(response) || 'No se pudo registrar la firma. Intenta de nuevo.';
        this.submitting = false;
        return;
      }

      await this.writeSignatureToFirebase(response);
      this.enterWaiting();
    } catch (error: any) {
      this.errorMessage = this.extractError(error?.error) || 'No se pudo registrar la firma. Intenta de nuevo.';
    } finally {
      this.submitting = false;
    }
  }

  private async writeSignatureToFirebase(response: any): Promise<void> {
    const emissionLogId = this.emissionLogId ?? (Number(response.emission_log_id) || null);
    this.emissionLogId = emissionLogId;
    if (!emissionLogId || !this.firebaseSync.isConfigured()) {
      return;
    }

    const attachmentId = Number(response.attachment_id);
    try {
      await this.firebaseSync.writeAssistanceSignature(emissionLogId, {
        attachmentId: Number.isFinite(attachmentId) ? attachmentId : null,
        signedAt: this.str(response.signed_at) || new Date().toISOString(),
        signerName: `${this.customer['firstName']} ${this.customer['lastName']}`.trim()
      });
    } catch {
      // Non-fatal: EmissionLog.status=2 is the source of truth; a reload recovers.
    }
  }

  private enterWaiting(): void {
    this.state = 'signedWaiting';
    if (!this.emissionLogId || !this.firebaseSync.isConfigured()) {
      return;
    }

    this.completionSubscription?.unsubscribe();
    this.completionSubscription = this.firebaseSync
      .watchAssistanceCompletion(this.emissionLogId, this.origin)
      .subscribe({
        next: status => {
          if (status.status === 1 || status.stage === 'emitted') {
            this.policyNumber = status.policyNumber || this.policyNumber;
            this.transactionNumber = status.transactionId || this.transactionNumber || this.policyNumber;
            this.state = 'emitted';
            this.completionSubscription?.unsubscribe();
            return;
          }

          // Seller rejected the signature: return to the signature step to re-sign.
          // Trigger on stage/status only — the `rejected` flag is stale after the
          // customer re-signs (Firebase update merges), so relying on it would loop.
          if (this.state === 'signedWaiting' && (status.stage === 'pending' || status.status === 0)) {
            this.rejectionReason = status.rejectedReason || 'No validada';
            this.signatureImage = null;
            this.errorMessage = '';
            this.state = 'pending';
          }
        },
        error: () => {
          // Keep the waiting screen; a reload reconstructs status from get_muvit_private.
        }
      });
  }

  get canDownloadDocument(): boolean {
    return !!this.policyNumber && !!this.transactionNumber;
  }

  printDocument(): void {
    if (this.isPrintingDocument) {
      return;
    }
    if (!this.canDownloadDocument) {
      this.documentStatusType = 'error';
      this.documentStatusMessage = 'No se encontró un número de póliza válido para imprimir el documento.';
      return;
    }

    this.isPrintingDocument = true;
    this.documentStatusMessage = '';
    // Public GET endpoint (no auth header needed): open the signed document directly.
    const win = window.open(
      this.api.agricola.publicAssistanceDocumentUrl(this.policyNumber, this.transactionNumber),
      '_blank'
    );
    this.isPrintingDocument = false;

    if (win) {
      this.documentStatusType = 'success';
      this.documentStatusMessage = 'El documento se generó correctamente.';
    } else {
      this.documentStatusType = 'error';
      this.documentStatusMessage = 'No se pudo abrir el documento. Habilita las ventanas emergentes e inténtalo de nuevo.';
    }
  }

  private fail(message: string): void {
    this.state = 'error';
    this.errorMessage = message;
  }

  // The apiserver wraps every proxied response as { data: <s2 payload> }
  // (wrappers.api_response). Unwrap one level so we read the S2 fields (code,
  // status, data, emission_log_id) directly.
  private unwrapEnvelope(response: any): any {
    if (response && typeof response === 'object' && response.code === undefined
      && response.data && typeof response.data === 'object') {
      return response.data;
    }
    return response;
  }

  private str(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  private extraDataValue(key: string): string {
    const list = this.dataToSend?.extraData;
    if (!Array.isArray(list)) {
      return '';
    }
    const item = list.find((entry: any) => entry && entry.key === key);
    return item ? this.str(item.value) : '';
  }

  private documentTypeLabel(value: string): string {
    const normalized = value.toLowerCase();
    const labels: Record<string, string> = {
      dui: 'DUI',
      nit: 'NIT',
      passport: 'Pasaporte',
      pasaporte: 'Pasaporte',
      carnet: 'Carné de residente',
      residente: 'Carné de residente'
    };
    return labels[normalized] || (value ? value.toUpperCase() : '');
  }

  // Normalizes a dependent birthday (ISO `yyyy-mm-dd` or `dd/mm/yyyy`) into the
  // `dd/mm/yyyy` shown on the public page. Leaves unrecognized values untouched.
  private formatBirthday(value: unknown): string {
    const raw = this.str(value);
    if (!raw) {
      return '';
    }

    const isoParts = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
    if (isoParts) {
      return `${isoParts[3].padStart(2, '0')}/${isoParts[2].padStart(2, '0')}/${isoParts[1]}`;
    }

    const slashParts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
    if (slashParts) {
      return `${slashParts[1].padStart(2, '0')}/${slashParts[2].padStart(2, '0')}/${slashParts[3]}`;
    }

    return raw;
  }

  private genderLabel(gender: unknown): string {
    if (gender === 1 || gender === '1') {
      return 'Femenino';
    }
    if (gender === 0 || gender === '0') {
      return 'Masculino';
    }
    return '';
  }

  private isEnabled(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true' || value === 'yes';
  }

  private formatPrice(price: number, currency: string): string {
    if (!price || price === 0) {
      return 'Gratuito';
    }
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(price));
    } catch {
      return `${currency || ''} ${price}`.trim();
    }
  }

  private extractError(payload: any): string {
    if (!payload) {
      return '';
    }
    return this.str(payload.message || payload.error || payload.descripcion);
  }

  private dataURLtoBlob(dataURL: string): Blob {
    const [meta, b64] = dataURL.split(',');
    const mime = meta.split(':')[1].split(';')[0];
    const bytes = atob(b64);
    const buffer = new ArrayBuffer(bytes.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      view[i] = bytes.charCodeAt(i);
    }
    return new Blob([buffer], { type: mime });
  }
}
